import { useState, useRef } from 'react';
import { ChevronRight, X, ShieldAlert, BookOpen, AlertCircle } from 'lucide-react';
import IdUploadStep from './IdUploadStep';
import FaceVerifyStep from './FaceVerifyStep';
import ProctoringCalibrationStep from './ProctoringCalibrationStep';
import EnvironmentVerifyStep from './EnvironmentVerifyStep';
import { useAuth } from '../../../../contexts/authContext';

const PreQuizSetupFlow = ({ quiz, onComplete, onCancel }) => {
  const { profile } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const baselinesRef = useRef(null);

  // Dynamically build steps
  const steps = [];
  if (quiz.enforce_id_verification) {
    if (!profile?.student_id_card) {
      steps.push('id_upload');
    }
    steps.push('face_verify');
  }
  if (quiz.proctoring_enabled) {
    steps.push('calibration');
  }
  if (quiz.environment_check_enabled) {
    steps.push('environment_verify');
  }
  steps.push('instructions');

  const currentStep = steps[currentStepIndex];

  const handleNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handleCalibrationComplete = (baselines) => {
    baselinesRef.current = baselines;
    handleNextStep();
  };

  const handleStartExam = () => {
    onComplete({ baselines: baselinesRef.current });
  };

  // Human-readable labels for progress tracker
  const getStepLabel = (stepKey) => {
    switch (stepKey) {
      case 'id_upload': return 'Student ID Upload';
      case 'face_verify': return 'Face Verification';
      case 'calibration': return 'Camera Calibration';
      case 'environment_verify': return 'Environment Verification';
      case 'instructions': return 'Exam Instructions';
      default: return '';
    }
  };

  return (
    <div className="card bg-base-100 shadow-2xl border border-base-300 max-w-2xl mx-auto overflow-hidden animate-fade-in my-8">
      {/* Premium Gradient Top Border */}
      <div className="h-1.5 bg-gradient-to-r from-primary via-secondary to-accent" />
      
      <div className="card-body p-6 sm:p-8 space-y-6">
        
        {/* Header Block */}
        <div className="flex justify-between items-start gap-4">
          <div className="space-y-1">
            <span className="text-xs uppercase tracking-wider font-semibold text-primary">Pre-Exam Check & Setup</span>
            <h1 className="text-2xl font-bold tracking-tight">{quiz?.topic_detail?.title || quiz?.course_detail?.title || quiz?.title || 'Quiz Access Setup'}</h1>
            <p className="text-sm text-gray-500">Please complete all verification and setup steps before starting your exam.</p>
          </div>
          {onCancel && (
            <button 
              onClick={onCancel}
              className="btn btn-circle btn-ghost btn-sm"
              aria-label="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress Tracker Tracker */}
        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm bg-base-200 p-2.5 rounded-lg border border-base-300">
          {steps.map((stepKey, index) => (
            <div key={stepKey} className="flex items-center gap-1.5">
              {index > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-md font-semibold ${
                currentStepIndex === index 
                  ? 'bg-primary text-primary-content' 
                  : currentStepIndex > index 
                    ? 'text-success font-bold' 
                    : 'text-gray-500'
              }`}>
                <span className={`w-4 h-4 text-[10px] flex items-center justify-center rounded-full ${
                  currentStepIndex === index 
                    ? 'bg-base-100 text-black' 
                    : currentStepIndex > index 
                      ? 'bg-success text-success-content' 
                      : 'bg-base-300 text-gray-600'
                }`}>
                  {currentStepIndex > index ? '✓' : index + 1}
                </span>
                <span>{getStepLabel(stepKey)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Step Views */}
        {currentStep === 'id_upload' && (
          <IdUploadStep 
            onNext={handleNextStep}
            onCancel={onCancel}
          />
        )}

        {currentStep === 'face_verify' && (
          <FaceVerifyStep 
            quizId={quiz.id}
            onSuccess={handleNextStep}
            onCancel={onCancel}
          />
        )}

        {currentStep === 'calibration' && (
          <ProctoringCalibrationStep 
            onComplete={handleCalibrationComplete}
            onCancel={onCancel}
          />
        )}

        {currentStep === 'environment_verify' && (
          <EnvironmentVerifyStep 
            quizId={quiz.id}
            onNext={handleNextStep}
            onCancel={onCancel}
          />
        )}

        {currentStep === 'instructions' && (
          <div className="space-y-6 animate-fade-in">
            <div className="space-y-2">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Exam Rules & Instructions
              </h2>
              <p className="text-sm text-gray-500">
                Please read and confirm you understand the guidelines below before launching the exam.
              </p>
            </div>

            <div className="space-y-4">
              {/* Fullscreen & Switch Warning */}
              {quiz.quiz_type === "exam" && (
                <div className="border border-warning/20 text-xs border-l-4 border-warning bg-warning/5 text-base-content rounded-xl flex gap-3 p-4 items-start">
                  <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5 text-warning" />
                  <div>
                    <h4 className="font-bold text-sm text-warning">Security Controls Enforced</h4>
                    <p className="mt-1 leading-relaxed text-base-content/85">
                      This is a secured examination. The following rules are strictly enforced:
                    </p>
                    <ul className="list-disc pl-5 mt-2 space-y-1.5 leading-relaxed text-base-content/80">
                      <li><strong>Fullscreen Mode</strong>: The exam will enter fullscreen. If you exit fullscreen more than 3 times, your exam will be automatically submitted.</li>
                      <li><strong>Tab Switching</strong>: Switching tabs, windows, or applications is prohibited. Exceeding 3 switches will trigger automatic submission.</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Proctoring Warning */}
              {quiz.proctoring_enabled && (
                <div className="border border-error/20 text-xs border-l-4 border-error bg-error/5 text-base-content rounded-xl flex gap-3 p-4 items-start">
                  <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5 text-error" />
                  <div>
                    <h4 className="font-bold text-sm text-error">AI Video Proctoring Active</h4>
                    <p className="mt-1 leading-relaxed text-base-content/85">
                      Your webcam will remain active to monitor compliance:
                    </p>
                    <ul className="list-disc pl-5 mt-2 space-y-1.5 leading-relaxed text-base-content/80">
                      <li>Stay in center frame at all times.</li>
                      <li>Ensure you are alone in the room with no background screens or other people.</li>
                      <li>Avoid looking away or turning your head excessively.</li>
                      <li>Violations are logged automatically and flagged for lecturer review.</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="border border-info/20 text-xs border-l-4 border-info bg-info/5 text-base-content rounded-xl flex gap-3 p-4 items-start">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-info" />
                <div>
                  <h4 className="font-bold text-sm text-info">General Instructions</h4>
                  <ul className="list-disc pl-5 mt-1.5 space-y-1 leading-relaxed text-base-content/80">
                    <li>Duration: <strong>{quiz.expected_duration ? quiz.expected_duration.split(':').slice(0,2).join('h ') + 'm' : 'Untimed'}</strong>.</li>
                    <li>Ensure you have a stable internet connection.</li>
                    <li>Once you start, the timer cannot be paused.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              {onCancel && (
                <button 
                  type="button" 
                  onClick={onCancel} 
                  className="btn btn-outline flex-1 rounded-xl font-bold"
                >
                  Go Back
                </button>
              )}
              <button 
                type="button" 
                onClick={handleStartExam} 
                className="btn btn-primary flex-[2] rounded-xl font-bold gap-2 py-3.5 h-auto min-h-0 text-base shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-200"
              >
                Start Exam
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PreQuizSetupFlow;
